import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { hasProdLogsEnabled } from './utils/helper';

export type C1nxReduxState = {
  c1Common: {
    theme: { colorMode: string; overrides: Record<string, never> };
    storeId: string;
    locale: string;
  };
  c1Token: {
    expires: string;
    role: string;
    roleName: string;
    userUrn: string;
    serviceRoleUrnList: any[];
    token: string;
  };
  c1Services: {
    hasError: boolean;
    fetchServices: boolean;
    c1ServicesList: {
      id: string;
      category: string;
      state: string;
      name: string;
      description: string;
      links: {
        rel: string;
        href: string;
      }[];
      license?: 'trial' | 'unlicensed' | 'licensed';
      // Date
      expires?: string;
      expired?: boolean;
    }[];
  };
  c1UserDetails: { timezone: string; name: string; type: string; email: string; wsAdminID: string };
  c1Accounts: {
    c1AccountID: string;
    c1Central: { projectID: string };
    c1AccountsList: [
      {
        id: string;
        alias: string;
        locale: string;
        timezone: string;
        region: string;
        state: string;
        created: string;
        lastModified: string;
        urn: string;
        mfaRequired: boolean;
        links: {
          rel: string;
          href: string;
          method: string;
        }[];
      }
    ];
    c1CloudProviders:
      | {
          [provider in 'aws']: Array<CloudProviderAccount> | undefined;
        }
      | undefined;
  };
};

/**
 * Redux Store: web-app-c1nx
 **/
const initialState = {
  c1Accounts: {
    c1AccountID: '',
    c1AccountsList: [],
    c1CloudProviders: {},
    c1Central: { projectID: '' },
  },
  c1Services: { c1ServicesList: [] },
  c1Token: { token: '', expires: '', userUrn: '', role: '', roleName: '', serviceRoleUrnList: [] },
  c1UserDetails: { name: '', email: '', timezone: '', wsAdminID: '', type: '' },
  c1Common: {
    storeId: 'web-app-c1nx',
    locale: 'en',
    theme: { colorMode: 'light', overrides: {} },
  },
};

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case 'ADD_PROJECT':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1Central: {
            projectID: action.id,
          },
        },
      };

    case 'ADD_LAST_SELECTED_ACCOUNT_ID':
    case 'ADD_ACCOUNT_ID':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1AccountID: action.c1AccountID,
          c1CloudProviders: {},
          c1Central: {
            projectID: '',
          },
        },
      };
    case 'ADD_USER_TYPE':
      return {
        ...state,
        c1UserDetails: {
          ...state.c1UserDetails,
          type: action.userType,
        },
      };
    case 'ADD_USER_INFO': {
      const { name = '', email = '', timezone = '' } = action.userDetails;
      return {
        ...state,
        c1UserDetails: {
          ...state.c1UserDetails,
          name,
          email,
          timezone,
        },
      };
    }
    case 'STORE_ACCOUNTS':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1AccountsList: action.storeAccounts,
        },
      };
    case 'STORE_INIT':
      // rehydrate the states base on shell state (e.g. upon refresh)
      return {
        ...action.shellInitState,
        c1Common: {
          ...action.shellInitState.c1Common,
          storeId: 'web-app-c1nx',
        },
      };
    case 'SET_LOCALE':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          locale: action.locale,
        },
      };
    case 'STORE_SERVICES':
      return {
        ...state,
        c1Services: {
          ...state.c1Services,
          c1ServicesList: action.c1ServicesList,
        },
      };
    case 'STORE_C1_TOKEN':
      return {
        ...state,
        c1Token: {
          ...action.c1Token,
        },
      };
    case 'SET_THEME':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: action.theme,
        },
      };
    case 'SET_COLOR_MODE':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: {
            ...state.c1Common.theme,
            colorMode: action.colorMode,
          },
        },
      };
    case 'SET_THEME_OVERRIDES':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: {
            ...state.c1Common.theme,
            overrides: action.overrides,
          },
        },
      };
    case 'UPDATE_CLOUD_PROVIDER_ACCOUNTS':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1CloudProviders: {
            ...state.c1Accounts.c1CloudProviders,
            [action.provider]: action.accounts,
          },
        },
      };
    case 'HYDRATE_STORE':
      return {
        ...state,
        ...action.storeState,
      };
    case 'RESET_STORE':
      return {
        ...state,
        ...initialState,
      };
    default:
      return state;
  }
};

export const getTokenInfo = (state, type) => state?.c1Token?.[type];
export const getCurrentAccountID = (state) => state?.c1Accounts?.c1AccountID;
export const getCommonType = <K extends keyof C1nxReduxState['c1Common']>(
  state,
  type: K
): C1nxReduxState['c1Common'][K] => state?.c1Common?.[type];
export const getServices = <K extends keyof C1nxReduxState['c1Services']>(
  state,
  type: K
): C1nxReduxState['c1Services'][K] => state?.c1Services?.[type];
export const getUserDetails = (state, type) => state?.c1UserDetails?.[type];

export const getAccountInfo = <K extends keyof C1nxReduxState['c1Accounts']['c1AccountsList'][0]>(
  state,
  key: K,
  accountID?: string
) => {
  const currentAccountID = accountID ?? state?.c1Accounts?.c1AccountID;
  const accountInfo = state?.c1Accounts?.c1AccountsList.find(
    (account) => account.id === currentAccountID
  );
  return accountInfo?.[key];
};

export const getAccounts = <K extends keyof C1nxReduxState['c1Accounts']>(
  state,
  type: K
): C1nxReduxState['c1Accounts'][K] | undefined => state?.c1Accounts?.[type];

export const getProject = (state): C1nxReduxState['c1Accounts']['c1Central']['projectID'] =>
  state?.c1Accounts?.c1Central.projectID;

/**
 * Custom Logger Middleware
 * @param store
 * @returns {function(*): function(*=): *}
 */
/**
 * Logs all actions and states after they are dispatched.
 */
const logger = (store) => (next) => (action) => {
  if (
    (DEPLOYMENT_TARGET !== 'prod' && DEPLOYMENT_TARGET !== 'govcloud-prod') ||
    hasProdLogsEnabled
  ) {
    console.group(action.type, 'web-app-c1nx');
    console.debug('web-app-c1nx dispatching', action);

    // Call the next dispatch method in the middleware chain
    const result = next(action);

    console.debug('web-app-c1nx next state after dispatch', store.getState());
    console.groupEnd();
    return result;
  }
  return next(action);
};

const composeEnhancers = composeWithDevTools({ name: 'web-app-c1nx' });
export const storeInstance = createStore(reducer, composeEnhancers(applyMiddleware(logger)));
